/**
 * ImportantNewsWidget — Dashboard-Karte mit
 * (1) angepinnten News-Beiträgen (max. 3) und
 * (2) ungelesenen Pflicht-Lesebestätigungen.
 * Klick auf einen Beitrag öffnet den Newsfeed mit Tiefenlink.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Pin, CheckCircle2, MessageSquare, Newspaper, AlertCircle, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  if (diffMin < 60 * 24) return `vor ${Math.floor(diffMin / 60)} Std.`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

const PostRow = ({ post, onConfirm, onOpen, dataTestid }) => (
  <div
    className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer"
    onClick={() => onOpen(post.id)}
    data-testid={dataTestid}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        {post.is_pinned && (
          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 gap-1">
            <Pin className="h-3 w-3" /> Angepinnt
          </Badge>
        )}
        {post.require_confirmation && !post.user_confirmed && (
          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 gap-1">
            <AlertCircle className="h-3 w-3" /> Bestätigung erforderlich
          </Badge>
        )}
      </div>
      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
        {post.title}
      </p>
      {post.content_preview && (
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {post.content_preview}
        </p>
      )}
      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
        <span>{post.author_name}</span>
        <span>·</span>
        <span>{formatDate(post.created_at)}</span>
        {post.comment_count > 0 && (
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {post.comment_count}
          </span>
        )}
      </div>
    </div>
    {post.require_confirmation && !post.user_confirmed && onConfirm && (
      <Button
        size="sm"
        variant="outline"
        className="shrink-0 h-8"
        onClick={(e) => { e.stopPropagation(); onConfirm(post.id); }}
        data-testid={`confirm-${post.id}`}
      >
        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Gelesen
      </Button>
    )}
  </div>
);

export const ImportantNewsWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ pinned_posts: [], unread_required: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/news/dashboard-summary');
      setData(res.data);
    } catch (e) {
      // News-Berechtigung fehlt → Widget einfach nicht anzeigen
      if (e.response?.status === 403) {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async (postId) => {
    try {
      await api.post(`/news/${postId}/confirm`);
      toast.success('Lesebestätigung gespeichert');
      load();
    } catch {
      toast.error('Bestätigung fehlgeschlagen');
    }
  };

  const handleOpen = (postId) => {
    navigate(`/news?post=${postId}`);
  };

  if (loading) {
    return (
      <Card className="border-border/60">
        <CardHeader className="border-b border-border/40 pb-4">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;
  const totalCount = data.pinned_posts.length + data.unread_required.length;
  if (totalCount === 0) return null;

  // Doppelte Einträge vermeiden (ein Post kann gepinnt UND bestätigungspflichtig sein)
  const pinnedIds = new Set(data.pinned_posts.map((p) => p.id));
  const uniqueUnread = data.unread_required.filter((p) => !pinnedIds.has(p.id));

  return (
    <Card className="border-border/60" data-testid="important-news-widget">
      <CardHeader className="border-b border-border/40 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <CardTitle className="font-heading text-lg flex flex-wrap items-center gap-2">
          <Newspaper className="h-5 w-5 text-primary shrink-0" />
          Wichtige Beiträge
          {data.unread_required.length > 0 && (
            <Badge variant="destructive" className="ml-1">
              {data.unread_required.length} ungelesen
            </Badge>
          )}
        </CardTitle>
        <Link to="/news" className="shrink-0 self-start sm:self-auto">
          <Button variant="ghost" size="sm" className="text-xs whitespace-nowrap" data-testid="news-widget-view-all">
            Alle <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="p-2 divide-y divide-border/40">
        {uniqueUnread.length > 0 && (
          <div className="pb-2">
            <p className="text-xs font-medium text-muted-foreground px-3 py-2 uppercase tracking-wide">
              Lesebestätigung erforderlich
            </p>
            {uniqueUnread.map((p) => (
              <PostRow
                key={`unread-${p.id}`}
                post={p}
                onConfirm={handleConfirm}
                onOpen={handleOpen}
                dataTestid={`unread-post-${p.id}`}
              />
            ))}
          </div>
        )}
        {data.pinned_posts.length > 0 && (
          <div className="pt-2">
            <p className="text-xs font-medium text-muted-foreground px-3 py-2 uppercase tracking-wide">
              Angepinnte Beiträge
            </p>
            {data.pinned_posts.map((p) => (
              <PostRow
                key={`pinned-${p.id}`}
                post={p}
                onConfirm={p.require_confirmation && !p.user_confirmed ? handleConfirm : null}
                onOpen={handleOpen}
                dataTestid={`pinned-post-${p.id}`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
